// Iter v34z (#2) — Vendor PO (Pembuatan PO Vendor) — procurement umum (non-batching).
// Format mengikuti template PT Rizal Bersaudara Berkah. Nomor PO auto-generate PO-PRC-YYMM-####.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Copy, RefreshCw, X } from "lucide-react";
import PdfViewerModal from "@/components/pdf/PdfViewerModal";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button, Field, Input, Modal, Textarea, Select } from "@/components/ui-kit";
import MasterCombo from "@/components/MasterCombo";
import SignatoryPicker from "@/components/SignatoryPicker";
import { usePagination } from "@/hooks/usePagination";
import { formatIDR, formatDate, parseIDR, parseQty, todayISO, formatQty } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";

const DEFAULT_TERMS = [
  "1. Pihak Supplier wajib melampirkan Surat Jalan asli dan copy PO ini saat penagihan.",
  "2. Barang yang dikirim harus sesuai dengan spesifikasi dan standar kualitas yang tertera pada PO.",
  "3. Barang cacat, rusak, atau tidak sesuai pesanan akan dikembalikan (Retur) atas biaya supplier.",
  "4. Batas maksimal keterlambatan pengiriman adalah 3 hari dari Tanggal Kirim, lewat dari itu PO dapat dibatalkan sepihak.",
].join("\n");

const blankItem = { material_name: "", spec: "", quantity: 1, unit: "Pcs", unit_price: 0 };
const blank = {
  po_number: "",
  date: todayISO(),
  delivery_date: "",
  vendor_name: "",
  vendor_address: "",
  vendor_contact: "",
  vendor_phone: "",
  ship_to: "",
  payment_term: "Net 30 Days",
  items: [{ ...blankItem }],
  company_id: "",
  company_name: "",
  company_npwp: "",
  use_vat: true,
  vat_rate: 11,
  terms_conditions: DEFAULT_TERMS,
  signatory_prepared: "Procurement / Purchasing",
  signatory_approved: "Manager Operasional",
  signatory_received: "Tanda Tangan & Cap Perusahaan",
  name_prepared: "",
  name_approved: "",
  name_received: "",
  show_spec: true,
};



export default function VendorPO() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);  // Iter v34z (#5) — master vendor list
  const [companies, setCompanies] = useState([]);  // Iter v34cq (#5) — perusahaan penerbit (NPWP)
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank);
  // Iter 65 (#3) — popout PDF viewer.
  const [viewer, setViewer] = useState(null);

  const load = () => api.get("/vendor-pos").then((r) => setRows(r.data || []));

  useEffect(() => {
    load();
    api.get("/vendors", { params: { active_only: true } }).then((r) => setVendors(r.data || [])).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  // Iter v34z (#5) — when user picks a vendor from dropdown, autofill 4 fields.
  const pickVendor = (vid) => {
    if (!vid) return;
    const v = vendors.find((x) => x.id === vid);
    if (!v) return;
    setForm((f) => ({
      ...f,
      vendor_name: v.name || "",
      vendor_address: v.address || "",
      vendor_contact: v.contact || "",
      vendor_phone: v.phone || "",
    }));
    toast.success(`Vendor "${v.name}" diisi otomatis`);
  };

  const setItem = (i, key, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [key]: val };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...blankItem }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  const subtotal = useMemo(
    () => form.items.reduce((s, it) => s + (parseQty(it.quantity) * parseIDR(it.unit_price)), 0),
    [form.items]
  );
  const vatRate = parseFloat(form.vat_rate) || 0;
  const vat = form.use_vat ? subtotal * vatRate / 100 : 0;
  const grand = subtotal + vat;

  const suggestNextNumber = async () => {
    try {
      const r = await api.get("/vendor-pos/next-number");
      if (r.data?.po_number) setForm((f) => ({ ...f, po_number: r.data.po_number }));
    } catch (e) { toast.error("Gagal mengambil nomor PO"); }
  };

  const openNew = async () => {
    setEdit(null);
    setForm({ ...blank, items: [{ ...blankItem }] });
    setOpen(true);
    // Auto-fetch next number for convenience
    try {
      const r = await api.get("/vendor-pos/next-number");
      if (r.data?.po_number) setForm((f) => ({ ...f, po_number: r.data.po_number }));
    } catch { /* ignore */ }
  };

  const _displayItem = (it) => ({
    ...it,
    quantity: it.quantity !== undefined && it.quantity !== null ? formatQty(it.quantity) : "",
    unit_price: it.unit_price !== undefined && it.unit_price !== null ? formatQty(it.unit_price) : "",
  });

  const openEdit = (p) => {
    setEdit(p);
    setForm({
      ...blank, ...p,
      items: (p.items || []).map((i) => _displayItem({ ...blankItem, ...i })),
    });
    setOpen(true);
  };

  const openDuplicate = (p) => {
    setEdit(null);
    setForm({
      ...blank, ...p,
      po_number: "", date: todayISO(),
      items: (p.items || []).map((i) => _displayItem({ ...blankItem, ...i })),
    });
    setOpen(true);
    toast.success("Duplikat siap — nomor PO akan dibuat ulang otomatis saat disimpan");
  };

  const submit = async () => {
    if (!form.vendor_name) { toast.error("Nama Vendor wajib diisi"); return; }
    if (form.items.length === 0 || !form.items[0].material_name) { toast.error("Tambahkan minimal 1 item"); return; }
    const payload = {
      ...form,
      vat_rate: parseFloat(form.vat_rate) || 0,
      items: form.items.map((it) => ({
        material_name: it.material_name,
        spec: it.spec || "",
        quantity: parseQty(it.quantity),
        unit: it.unit || "Pcs",
        unit_price: parseIDR(it.unit_price),
      })),
    };
    try {
      if (edit) await api.patch(`/vendor-pos/${edit.id}`, payload);
      else await api.post("/vendor-pos", payload);
      toast.success(edit ? "PO Vendor diperbarui" : "PO Vendor dibuat");
      setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus PO Vendor?")) return;
    await api.delete(`/vendor-pos/${id}`);
    toast.success("Dihapus"); load();
  };

  const downloadPdf = async (id, num, vendor) => {
    try {
      const r = await api.get(`/vendor-pos/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (s) => String(s || "").replace(/[^A-Za-z0-9._-]+/g, "_").replace(/_+/g, "_");
      a.download = `po_vendor_${safe(num || id)}${vendor ? `_${safe(vendor)}` : ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal download PDF"); }
  };

  const printPdf = async (id) => {
    try {
      const r = await api.get(`/vendor-pos/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch { /* ignore */ } });
    } catch (e) { toast.error("Gagal cetak PDF"); }
  };

  const { pageItems, Pagination } = usePagination(rows, { perPage: 50 });

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Procurement · PO Vendor"
        title="Pembuatan PO Vendor"
        description="Purchase Order untuk supplier umum (non-batching). Nomor PO terisi otomatis (PO-PRC-YYMM-####)."
        action={hasPerm("vendor_po.create") && (
          <Button onClick={openNew} data-testid="add-vendor-po-btn">
            <Plus className="h-4 w-4" /> PO Vendor Baru
          </Button>
        )}
      />

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">No PO</th>
              <th className="py-3 px-3">Tanggal</th>
              <th className="py-3 px-3">Vendor</th>
              <th className="py-3 px-3">Tgl Kirim</th>
              <th className="py-3 px-3">Termin</th>
              <th className="py-3 px-3 text-right">Grand Total</th>
              <th className="py-3 px-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="py-12 text-center text-stone-400">Belum ada PO Vendor.</td></tr>
            )}
            {pageItems.map((p, idx) => (
              <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`vendor-po-row-${idx}`}>
                <td className="py-2.5 px-3 font-mono font-medium">{p.po_number}</td>
                <td className="py-2.5 px-3 font-mono text-stone-600">{formatDate(p.date)}</td>
                <td className="py-2.5 px-3 font-medium">{p.vendor_name}</td>
                <td className="py-2.5 px-3 font-mono text-stone-500">{p.delivery_date ? formatDate(p.delivery_date) : "—"}</td>
                <td className="py-2.5 px-3 text-stone-600">{p.payment_term}</td>
                <td className="py-2.5 px-3 text-right font-mono-num font-semibold">{formatIDR(p.grand_total || 0)}</td>
                <td className="py-2.5 px-3 text-right">
                  <button onClick={() => setViewer(p)} className="text-stone-500 hover:text-terra-700 px-2" title="Lihat PDF" data-testid={`vendor-po-view-${idx}`}><Eye className="h-3.5 w-3.5" /></button>
                  {hasPerm("vendor_po.create") && <button onClick={() => openDuplicate(p)} className="text-stone-500 hover:text-terra-700 px-2" title="Duplikat" data-testid={`vendor-po-duplicate-${idx}`}><Copy className="h-3.5 w-3.5" /></button>}
                  {hasPerm("vendor_po.create") && <button onClick={() => openEdit(p)} className="text-stone-500 hover:text-terra-700 px-2" data-testid={`vendor-po-edit-${idx}`}><Pencil className="h-3.5 w-3.5" /></button>}
                  {hasPerm("vendor_po.create") && <button onClick={() => del(p.id)} className="text-stone-500 hover:text-red-700 px-2" data-testid={`vendor-po-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {Pagination}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit PO Vendor" : "Buat PO Vendor Baru"} size="xl">
        <div className="space-y-5">
          {/* Header: No PO / Tanggal / Tanggal Kirim / Termin */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="No. PO" required hint={!edit ? "Auto-generate (PO-PRC-YYMM-####)" : ""}>
              <div className="flex gap-1">
                <Input className="flex-1 font-mono" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="PO-PRC-2602-0001" data-testid="vendor-po-number" />
                <Button variant="secondary" type="button" onClick={suggestNextNumber} className="!px-2" title="Ambil nomor berikutnya" data-testid="vendor-po-suggest-num">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Field>
            <Field label="Tanggal PO" required>
              <DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="vendor-po-date" />
            </Field>
            <Field label="Tanggal Kirim">
              <DatePicker value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="vendor-po-delivery-date" />
            </Field>
            <Field label="Termin Bayar">
              <Input value={form.payment_term} onChange={(e) => setForm({ ...form, payment_term: e.target.value })} placeholder="Net 30 Days" data-testid="vendor-po-payment-term" />
            </Field>
          </div>

          {/* Iter v34cq (#5) — Perusahaan penerbit PO: nama + NPWP dari master Pengaturan. */}
          <div className="border border-stone-200 rounded-md p-4 bg-blue-50/30 space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Perusahaan Penerbit PO (Kop &amp; NPWP)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Pilih Perusahaan" hint="Dari Pengaturan → Database Perusahaan">
                <Select value={form.company_id || ""} onChange={(e) => {
                  const c = companies.find((x) => x.id === e.target.value);
                  setForm({
                    ...form,
                    company_id: e.target.value,
                    company_name: c?.name || "",
                    company_npwp: c?.npwp || "",
                  });
                }} data-testid="vendor-po-company-select">
                  <option value="">— Default (perusahaan utama) —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.npwp ? ` · NPWP ${c.npwp}` : ""}{c.is_default ? " (default)" : ""}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Nama Perusahaan">
                <Input value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="PT Rizal Bersaudara Berkah" data-testid="vendor-po-company-name" />
              </Field>
              <Field label="NPWP Perusahaan">
                <Input value={form.company_npwp || ""} onChange={(e) => setForm({ ...form, company_npwp: e.target.value })} placeholder="01.234.567.8-901.000" className="font-mono" data-testid="vendor-po-company-npwp" />
              </Field>
            </div>
          </div>

          {/* Vendor block */}
          <div className="border border-stone-200 rounded-md p-4 bg-stone-50/40 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Kepada (Vendor)</div>
              {/* Iter v34z (#5) — Quick-pick from Master Vendor */}
              {vendors.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <label className="text-stone-600">Pilih dari Master:</label>
                  <Select value="" onChange={(e) => pickVendor(e.target.value)} className="!py-1 !text-xs" data-testid="vendor-po-pick-vendor">
                    <option value="">— Pilih Vendor —</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Nama Vendor" required>
                <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} placeholder="PT. SINAR SUKSES DISTRIBUSI" data-testid="vendor-po-vendor-name" />
              </Field>
              <Field label="Up (Contact Person)">
                <Input value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} placeholder="Tim Sales / Marketing" data-testid="vendor-po-vendor-contact" />
              </Field>
              <Field label="Alamat Vendor">
                <Textarea rows={2} value={form.vendor_address} onChange={(e) => setForm({ ...form, vendor_address: e.target.value })} placeholder="Kawasan Industri Cikarang Blok C-21" data-testid="vendor-po-vendor-address" />
              </Field>
              <Field label="Telepon Vendor">
                <Input value={form.vendor_phone} onChange={(e) => setForm({ ...form, vendor_phone: e.target.value })} placeholder="(021) 8990-1234" data-testid="vendor-po-vendor-phone" />
              </Field>
            </div>
          </div>

          {/* Ship-to */}
          <Field label="Dikirim Ke (Ship To)">
            <Textarea rows={2} value={form.ship_to} onChange={(e) => setForm({ ...form, ship_to: e.target.value })} placeholder="Gudang Utama PT Rizal Bersaudara Berkah - JL. ..." data-testid="vendor-po-ship-to" />
          </Field>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Daftar Barang / Material</div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-stone-600">
                  <input type="checkbox" checked={!!form.show_spec} onChange={(e) => setForm({ ...form, show_spec: e.target.checked })} data-testid="vendor-po-toggle-spec" />
                  Tampilkan kolom Spesifikasi / PN
                </label>
                <Button variant="secondary" type="button" onClick={addItem} data-testid="vendor-po-add-item">
                  <Plus className="h-3.5 w-3.5" /> Tambah Item
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto border border-stone-200 rounded-md">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-100 text-left text-[10px] uppercase tracking-wider text-stone-600">
                    <th className="py-2 px-2 w-8">No</th>
                    <th className="py-2 px-2 min-w-[180px]">Nama Barang / Material</th>
                    {form.show_spec && <th className="py-2 px-2 min-w-[140px]">Spesifikasi / PN</th>}
                    <th className="py-2 px-2 w-20 text-right">Qty</th>
                    <th className="py-2 px-2 w-24">Satuan</th>
                    <th className="py-2 px-2 w-32 text-right">Harga Sat (Rp)</th>
                    <th className="py-2 px-2 w-32 text-right">Total</th>
                    <th className="py-2 px-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => {
                    const total = parseQty(it.quantity) * parseIDR(it.unit_price);
                    return (
                      <tr key={i} className="border-t border-stone-200" data-testid={`vendor-po-item-${i}`}>
                        <td className="py-1.5 px-2 text-stone-500">{i + 1}</td>
                        <td className="py-1.5 px-2">
                          <Input value={it.material_name} onChange={(e) => setItem(i, "material_name", e.target.value)} placeholder="Kabel NYY 4x10 mm" data-testid={`vendor-po-item-name-${i}`} />
                        </td>
                        {form.show_spec && (
                          <td className="py-1.5 px-2">
                            <Input value={it.spec} onChange={(e) => setItem(i, "spec", e.target.value)} placeholder="Supreme / Metal" data-testid={`vendor-po-item-spec-${i}`} />
                          </td>
                        )}
                        <td className="py-1.5 px-2">
                          <Input className="text-right" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`vendor-po-item-qty-${i}`} />
                        </td>
                        <td className="py-1.5 px-2">
                          <MasterCombo source="units" value={it.unit} onChange={(v) => setItem(i, "unit", v)} placeholder="Pcs" data-testid={`vendor-po-item-unit-${i}`} />
                        </td>
                        <td className="py-1.5 px-2">
                          <Input className="text-right" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`vendor-po-item-price-${i}`} />
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono-num text-stone-700">{formatIDR(total)}</td>
                        <td className="py-1.5 px-2 text-right">
                          {form.items.length > 1 && (
                            <button onClick={() => rmItem(i)} className="text-stone-400 hover:text-red-600" data-testid={`vendor-po-item-remove-${i}`}>
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-end gap-3 pt-2">
            <div className="md:w-80 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-stone-600">Subtotal</span>
                <span className="font-mono-num">{formatIDR(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-stone-600">
                  <input type="checkbox" checked={!!form.use_vat} onChange={(e) => setForm({ ...form, use_vat: e.target.checked })} data-testid="vendor-po-use-vat" />
                  PPN
                </label>
                <div className="flex items-center gap-1">
                  <Input type="number" className="!w-16 text-right" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} disabled={!form.use_vat} data-testid="vendor-po-vat-rate" />
                  <span className="text-stone-500 text-xs">%</span>
                  <span className="font-mono-num ml-2 min-w-[100px] text-right">{formatIDR(vat)}</span>
                </div>
              </div>
              <div className="flex justify-between border-t pt-2 border-stone-300 font-semibold text-base">
                <span>GRAND TOTAL</span>
                <span className="font-mono-num" data-testid="vendor-po-grand-total">{formatIDR(grand)}</span>
              </div>
            </div>
          </div>

          {/* Terms */}
          <Field label="Syarat dan Ketentuan">
            <Textarea rows={5} value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} data-testid="vendor-po-terms" />
          </Field>

          {/* Signatories — roles + printed names */}
          <div className="space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Penandatangan</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Dibuat Oleh (Jabatan)">
                <Input value={form.signatory_prepared} onChange={(e) => setForm({ ...form, signatory_prepared: e.target.value })} data-testid="vendor-po-sig-prepared" />
              </Field>
              <Field label="Disetujui Oleh (Jabatan)">
                <Input value={form.signatory_approved} onChange={(e) => setForm({ ...form, signatory_approved: e.target.value })} data-testid="vendor-po-sig-approved" />
              </Field>
              <Field label="Diterima & Disetujui (Jabatan)">
                <Input value={form.signatory_received} onChange={(e) => setForm({ ...form, signatory_received: e.target.value })} data-testid="vendor-po-sig-received" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Nama Penandatangan #1">
                <SignatoryPicker name={form.name_prepared}
                  onPick={({ name, position }) => setForm({ ...form, name_prepared: name, signatory_prepared: position || form.signatory_prepared })}
                  data-testid="vendor-po-name-prepared" />
              </Field>
              <Field label="Nama Penandatangan #2">
                <SignatoryPicker name={form.name_approved}
                  onPick={({ name, position }) => setForm({ ...form, name_approved: name, signatory_approved: position || form.signatory_approved })}
                  data-testid="vendor-po-name-approved" />
              </Field>
              <Field label="Nama Penandatangan #3 (Vendor)">
                <SignatoryPicker name={form.name_received}
                  onPick={({ name, position }) => setForm({ ...form, name_received: name, signatory_received: position || form.signatory_received })}
                  data-testid="vendor-po-name-received" />
              </Field>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="secondary" onClick={() => setOpen(false)} data-testid="vendor-po-cancel">Batal</Button>
            <Button onClick={submit} data-testid="vendor-po-submit">{edit ? "Update PO" : "Simpan PO"}</Button>
          </div>
        </div>
      </Modal>
      {/* Iter 65 (#3) — Popout PDF viewer */}
      <PdfViewerModal
        open={!!viewer}
        onClose={() => setViewer(null)}
        pdfUrl={viewer ? `${process.env.REACT_APP_BACKEND_URL}/api/vendor-pos/${viewer.id}/pdf` : ""}
        fileName={`VendorPO-${viewer?.po_number || viewer?.id || "doc"}.pdf`}
        title={`PO Vendor ${viewer?.po_number || ""}`}
      />
    </div>
  );
}
